import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Login from '@/components/Login';
import ProjectList from '@/components/ProjectList';
import ProjectDetail from '@/components/ProjectDetail';
import AdminPanel from '@/components/AdminPanel';
import DebugPanel from '@/components/DebugPanel';
import ErrorBoundary from '@/components/ErrorBoundary';
import UpdateNotification from '@/components/UpdateNotification';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
});

// Add auth interceptor
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add error logging interceptor for debug panel
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log error to debug panel
    const errorEvent = new CustomEvent('api-error', {
      detail: {
        timestamp: new Date().toISOString(),
        url: error.config?.url || 'Unknown',
        method: error.config?.method?.toUpperCase() || 'GET',
        status: error.response?.status || null,
        message: error.response?.data?.detail || error.message || 'Unknown error',
        data: error.response?.data || null,
        requestData: error.config?.data ? JSON.parse(error.config.data) : null,
      }
    });
    window.dispatchEvent(errorEvent);
    
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    // Listen for service worker update events
    const handleSWUpdate = (event) => {
      const registration = event.detail.registration;
      if (registration && registration.waiting) {
        setWaitingWorker(registration.waiting);
        setShowUpdateNotification(true);
      }
    };

    window.addEventListener('sw-update', handleSWUpdate);

    return () => {
      window.removeEventListener('sw-update', handleSWUpdate);
    };
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axiosInstance.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const handleRefresh = () => {
    if (waitingWorker) {
      // Tell the waiting worker to skip waiting and activate
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      // Reload the page to get the new version
      window.location.reload();
    }
  };

  const handleDismissUpdate = () => {
    setShowUpdateNotification(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="App">
        {showUpdateNotification && (
          <UpdateNotification
            onRefresh={handleRefresh}
            onDismiss={handleDismissUpdate}
          />
        )}
        <Toaster position="top-right" />
        <DebugPanel user={user} />
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
            />
            <Route
              path="/"
              element={
                user ? (
                  <ProjectList user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/projects/:projectId"
              element={
                user ? (
                  <ProjectDetail user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/admin"
              element={
                user?.role === 'admin' ? (
                  <AdminPanel user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/" />
                )
              }
            />
          </Routes>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
